import {
  type Active,
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  type Over,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import {
  BookOpen,
  Library,
  Plus,
  Settings2,
  Share2,
  Tag,
  Trash,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { focusOnPointerDown } from "@/lib/utils";
import { useUser } from "@/utils";
import { ConfirmationButton } from "../../ds/ConfirmationButton";
import AgeRatingBadge from "../../ds/icons/AgeRatingBadge";
import {
  canAddEpisode,
  canDeleteSeries,
  canEditEpisode,
  canEditSeries,
  canManageSharing,
} from "../../permissions";
import type { RegistryCategory, RegistrySeries } from "../../registry";
import { EpisodeCard } from "./EpisodeCard";
import { EpisodeFormDialog } from "./EpisodeFormDialog";
import { useDeleteSeries, useReorderEpisodes } from "./mutations";
import { SeriesFormDialog } from "./SeriesFormDialog";
import { ShareDialog } from "./ShareDialog";
import { SortableEpisodeCard } from "./SortableEpisodeCard";

interface SeriesDetailSheetProps {
  selectedSeries: RegistrySeries | null;
  onClose: () => void;
  categories: RegistryCategory[];
  showCategories: boolean;
}

export function SeriesDetailSheet({
  selectedSeries,
  onClose,
  categories,
  showCategories,
}: SeriesDetailSheetProps) {
  const { data: user } = useUser();
  const [editingSeries, setEditingSeries] = useState<RegistrySeries | null>(
    null,
  );
  const [showAddEpisode, setShowAddEpisode] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  // State rather than an imperative DOM mutation, which React clobbers.
  const [coverFailed, setCoverFailed] = useState(false);
  // The sheet's content has to outlive `selectedSeries` going null: Radix needs
  // it mounted to run the exit transition and hand focus back to the card.
  // Gating it on the same state as `open` tore it out first.
  const [shownSeries, setShownSeries] = useState(selectedSeries);
  useEffect(() => {
    if (selectedSeries) setShownSeries(selectedSeries);
  }, [selectedSeries]);

  const deleteSeries = useDeleteSeries();
  const reorderEpisodes = useReorderEpisodes(shownSeries?.pk ?? 0);

  // the pilot is always first and cannot be dragged out of that position
  const episodes = shownSeries?.episodeSet ?? [];
  const pilot = episodes.find((ep) => ep.isPilot);
  const sortableEpisodes = episodes.filter((ep) => !ep.isPilot);

  // renumbering an episode that already has recordings repoints it at a different
  // ch{N} folder on disk, which someone has to move by hand
  const riskyRenumbering =
    reorderEpisodes.data?.renumbered.filter((ep) => ep.risky) ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  // Used by the drag announcements below to name episodes rather than ids.
  const episodeLabel = (id: string | number) =>
    sortableEpisodes.find((ep) => ep.pk === id)?.label ?? "episode";
  const positionOf = (id: string | number) => {
    const index = sortableEpisodes.findIndex((ep) => ep.pk === id);
    // +1 for 1-based counting, and another if a pinned pilot precedes them.
    return index < 0 ? "unknown" : index + 1 + (pilot ? 1 : 0);
  };

  const horizontalScrollRef = useCallback((el: HTMLDivElement | null) => {
    const viewport = el?.closest<HTMLElement>(
      '[data-slot="scroll-area-viewport"]',
    );
    if (!viewport) return;
    const handler = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        viewport.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    viewport.addEventListener("wheel", handler, { passive: false });
    return () => viewport.removeEventListener("wheel", handler);
  }, []);

  return (
    <>
      <Sheet
        open={selectedSeries !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        {shownSeries && (
          <SheetContent
            side="bottom"
            className="h-auto max-h-[85vh]"
            aria-describedby={undefined}
          >
            <SheetHeader className="border-b">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                {shownSeries.cover && !coverFailed && (
                  <img
                    src={shownSeries.cover}
                    alt=""
                    className="w-full sm:w-32 h-auto rounded-md object-cover hidden sm:block"
                    onError={() => setCoverFailed(true)}
                  />
                )}
                <div className="flex flex-col gap-2 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <SheetTitle className="text-xl flex-1 flex gap-4 items-center">
                      <p>{shownSeries.title}</p>
                      <div className="flex flex-row gap-2">
                        {canEditSeries(shownSeries.userRole) && (
                          <Button
                            size="icon"
                            variant="secondary"
                            className="shrink-0"
                            onClick={() => setEditingSeries(shownSeries)}
                            aria-label="Edit series settings"
                          >
                            <Settings2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        )}
                        {canManageSharing(shownSeries.userRole) && (
                          <Button
                            size="icon"
                            variant="secondary"
                            className="shrink-0"
                            onClick={() => setShowShareDialog(true)}
                            aria-label="Share series"
                          >
                            <Share2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        )}
                        {canDeleteSeries(shownSeries.userRole) && (
                          <ConfirmationButton
                            size="icon"
                            variant="destructive"
                            className="shrink-0"
                            onClick={() => {
                              deleteSeries.mutate(shownSeries.pk, {
                                onSuccess: onClose,
                              });
                            }}
                            aria-label="Delete series"
                          >
                            <Trash className="h-4 w-4" aria-hidden="true" />
                          </ConfirmationButton>
                        )}
                      </div>
                    </SheetTitle>
                  </div>
                  {shownSeries.description && (
                    <p className="text-sm text-muted-foreground">
                      {shownSeries.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {shownSeries.genre && (
                      <Badge variant="secondary">
                        <Library />
                        {shownSeries.genre.name}
                      </Badge>
                    )}
                    {shownSeries.secondaryGenres?.map((g) => (
                      <Badge key={g.id} variant="default">
                        <Library />
                        {g.name}
                      </Badge>
                    ))}
                    {shownSeries.tags?.map((tag) => (
                      <Badge key={tag} variant="outline">
                        <Tag />
                        {tag}
                      </Badge>
                    ))}
                    {shownSeries.ageRestriction && (
                      <AgeRatingBadge value={shownSeries.ageRestriction} />
                    )}
                  </div>
                </div>
              </div>
            </SheetHeader>

            {shownSeries.episodeSet.length === 0 &&
            !canAddEpisode(shownSeries.userRole) ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
                <BookOpen className="h-8 w-8 opacity-40" />
                <p className="text-sm">No episodes available yet.</p>
              </div>
            ) : (
              <ScrollArea className="w-full">
                {riskyRenumbering.length > 0 && (
                  <div className="mx-6 mb-4 flex gap-2 rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-xs">
                    <div className="flex-1">
                      <p className="font-medium">
                        Renumbered episodes that already have content
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        Their audio, media and translations still live under the
                        old folder and must be moved by hand:
                      </p>
                      <ul className="mt-1 space-y-0.5 text-muted-foreground">
                        {riskyRenumbering.map((ep) => (
                          <li key={ep.pk}>
                            {ep.label}: ch{ep.oldNumber} → ch{ep.newNumber}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss"
                      onClick={() => reorderEpisodes.reset()}
                      className="h-4 shrink-0 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {reorderEpisodes.isError && (
                  <div className="mx-6 mb-4 rounded-md border border-danger-border bg-danger-surface px-3 py-2 text-xs">
                    {reorderEpisodes.error.message}
                  </div>
                )}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  // dnd-kit's defaults say "sortable item", which tells a
                  // screen-reader user nothing about which episode moved.
                  accessibility={{
                    screenReaderInstructions: {
                      draggable:
                        "To reorder an episode, press Space or Enter to pick it up, " +
                        "use the arrow keys to move it, then press Space or Enter " +
                        "again to drop it. Press Escape to cancel.",
                    },
                    announcements: {
                      onDragStart: ({ active }: { active: Active }) =>
                        `Picked up ${episodeLabel(active.id)}.`,
                      onDragOver: ({
                        active,
                        over,
                      }: {
                        active: Active;
                        over: Over | null;
                      }) =>
                        over
                          ? `${episodeLabel(active.id)} is over position ${positionOf(over.id)}.`
                          : `${episodeLabel(active.id)} is no longer over a drop target.`,
                      onDragEnd: ({
                        active,
                        over,
                      }: {
                        active: Active;
                        over: Over | null;
                      }) =>
                        over
                          ? `Dropped ${episodeLabel(active.id)} at position ${positionOf(over.id)}.`
                          : `Dropped ${episodeLabel(active.id)}. It was returned to its original position.`,
                      onDragCancel: ({ active }: { active: Active }) =>
                        `Reordering cancelled. ${episodeLabel(active.id)} returned to its original position.`,
                    },
                  }}
                  onDragEnd={(event: DragEndEvent) => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;
                    if (!canEditEpisode(shownSeries.userRole)) return;
                    const sortableOrder = sortableEpisodes.map((ep) => ep.pk);
                    const newOrder = arrayMove(
                      sortableOrder,
                      sortableOrder.indexOf(active.id as number),
                      sortableOrder.indexOf(over.id as number),
                    );
                    // the pilot is pinned first and never part of the drag
                    reorderEpisodes.mutate(
                      pilot ? [pilot.pk, ...newOrder] : newOrder,
                    );
                  }}
                >
                  <SortableContext
                    items={sortableEpisodes.map((ep) => ep.pk)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <div
                      ref={horizontalScrollRef}
                      className="flex gap-4 px-6 pb-6"
                    >
                      {pilot && (
                        <EpisodeCard
                          key={pilot.pk}
                          chapter={pilot}
                          genre={shownSeries.genre?.name}
                          userRole={shownSeries.userRole}
                        />
                      )}
                      {sortableEpisodes.map((episode) =>
                        canEditEpisode(shownSeries.userRole) ? (
                          <SortableEpisodeCard
                            key={episode.pk}
                            episode={episode}
                            genre={shownSeries.genre?.name}
                            userRole={shownSeries.userRole}
                          />
                        ) : (
                          <EpisodeCard
                            key={episode.pk}
                            chapter={episode}
                            genre={shownSeries.genre?.name}
                            userRole={shownSeries.userRole}
                          />
                        ),
                      )}
                      {canAddEpisode(shownSeries.userRole) && (
                        <button
                          type="button"
                          onClick={() => setShowAddEpisode(true)}
                          onPointerDown={focusOnPointerDown}
                          className="shrink-0 w-66 min-h-50 rounded-lg border-2 border-dashed border-muted-foreground/25 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground transition-colors cursor-pointer"
                        >
                          <Plus className="h-8 w-8" />
                          <span className="text-sm font-medium">
                            Add Episode
                          </span>
                        </button>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            )}
          </SheetContent>
        )}
      </Sheet>

      <SeriesFormDialog
        open={editingSeries !== null}
        onOpenChange={(open) => {
          if (!open) setEditingSeries(null);
        }}
        categories={categories}
        showCategories={showCategories}
        series={editingSeries ?? undefined}
      />

      {shownSeries && (
        <EpisodeFormDialog
          open={showAddEpisode}
          onOpenChange={setShowAddEpisode}
          seriesPk={shownSeries.pk}
        />
      )}

      {shownSeries && canManageSharing(shownSeries.userRole) && (
        <ShareDialog
          seriesPk={shownSeries.pk}
          seriesTitle={shownSeries.title}
          user={user}
          userRole={shownSeries.userRole}
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
        />
      )}
    </>
  );
}
